import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CategoryBadge from '../components/CategoryBadge';
import ReactionButtons from '../components/ReactionButtons';
import CreatePostModal from '../components/CreatePostModal';
import { getPostById, deletePost, getComments, addComment, deleteComment } from '../api/feedApi';
import { formatDate } from '../utils/dateFormatter';
import useAuthStore from '../../stores/authStore';
import './SinglePost.css';

const SinglePost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchPost = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getPostById(postId);
      if (response.success) {
        setPost(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await getComments(postId, { limit: 100 });
      if (response.success) {
        setComments(response.data.comments);
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  }, [postId]);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [fetchPost, fetchComments]);

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    if (!commentText.trim()) return;

    setSubmitting(true);

    try {
      const response = await addComment(postId, commentText.trim());
      if (response.success) {
        setComments([...comments, response.data]);
        setCommentText('');
        // Update comment count
        setPost({ ...post, commentsCount: post.commentsCount + 1 });
      }
    } catch (err) {
      alert(err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      await deletePost(postId);
      navigate('/feed');
    } catch (err) {
      alert(err.message || 'Failed to delete post');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await deleteComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      setPost({ ...post, commentsCount: post.commentsCount - 1 });
    } catch (err) {
      alert(err.message || 'Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="single-post-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="single-post-page">
        <div className="error-message">
          <p>{error || 'Post not found'}</p>
          <button onClick={() => navigate('/feed')}>Back to Feed</button>
        </div>
      </div>
    );
  }

  const isOwner = user && post.user.id === user.id;

  return (
    <div className="single-post-page">
      <div className="single-post-container">
        <button className="back-btn" onClick={() => navigate('/feed')}>
          ← Back to Feed
        </button>

        <div className="post-detail">
          <div className="post-detail-header">
            <div className="post-author-section">
              <img 
                src={post.user.avatarUrl || '/img/user-man.png'} 
                alt={post.user.username}
                className="author-avatar-large"
              />
              <div className="author-details">
                <h3>{post.user.fullName || post.user.username}</h3>
                <p>{formatDate(post.createdAt)}</p>
              </div>
            </div>
            <div className="post-header-actions">
              <CategoryBadge category={post.category} />
              {isOwner && (
                <div className="post-actions">
                  <button onClick={() => setShowEditModal(true)}>Edit</button>
                  <button onClick={handleDeletePost} className="delete-btn">Delete</button>
                </div>
              )}
            </div>
          </div>

          <h1 className="post-detail-title">{post.title}</h1>
          
          <div className="post-detail-content">
            {post.content}
          </div>

          {post.imageUrl && (
            <img 
              src={post.imageUrl} 
              alt={post.title}
              className="post-detail-image"
            />
          )}

          <div className="post-detail-footer">
            <ReactionButtons
              postId={post.id}
              initialLikes={post.likesCount}
              initialDislikes={post.dislikesCount}
              initialUserReaction={post.userReaction}
              onUpdate={(updated) => setPost({ ...post, ...updated })}
            />
          </div>
        </div>

        <div className="comments-section">
          <h2>Comments ({comments.length})</h2>

          {isAuthenticated ? (
            <form className="comment-form" onSubmit={handleAddComment}>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Write a comment..."
                rows={3}
                maxLength={2000}
              />
              <div className="comment-form-footer">
                <span className="char-count">{commentText.length}/2000</span>
                <button type="submit" disabled={submitting || !commentText.trim()}>
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </form>
          ) : (
            <div className="auth-prompt">
              <p>Please <a href="/signin">sign in</a> to comment</p>
            </div>
          )}

          <div className="comments-list">
            {comments.length === 0 ? (
              <p className="no-comments">No comments yet. Be the first to comment!</p>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className="comment-item">
                  <img 
                    src={comment.user.avatarUrl || '/img/user-man.png'} 
                    alt={comment.user.username}
                    className="comment-avatar"
                  />
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-author">{comment.user.fullName || comment.user.username}</span>
                      <span className="comment-time">{formatDate(comment.createdAt)}</span>
                    </div>
                    <p className="comment-text">{comment.content}</p>
                    {user && comment.user.id === user.id && (
                      <button 
                        className="delete-comment-btn"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showEditModal && (
        <CreatePostModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            fetchPost();
            setShowEditModal(false);
          }}
          editPost={post}
        />
      )}
    </div>
  );
};

export default SinglePost;
